<script setup>
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisLastUpdate from 'dashboard/components-next/LaisLastUpdate.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';

/**
 * LaisOperationalPage — the shell for every dense cockpit / list page.
 *
 * Composes LaisHeader for the title block and owns the page-level loading,
 * error and empty states so each domain screen stays focused on its data.
 * Slots: metrics (top strip), filters (filter bar row), default (scrollable
 * content), actions (forwarded into the header), aside (responsive right rail)
 * and footer (pinned action bar). Emits `back` (from the header) and `retry`
 * (from the error state).
 */
const props = defineProps({
  title: { type: String, default: '' },
  helper: { type: String, default: '' },
  breadcrumb: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: [Boolean, String], default: false },
  lastUpdatedAt: { type: [String, Number, Date], default: '' },
  showBack: { type: Boolean, default: false },
  asideLabel: { type: String, default: '' },
  skeletonCount: {
    type: Number,
    default: 3,
    validator: value => Number.isInteger(value) && value > 0,
  },
});

const emit = defineEmits(['back', 'retry']);

const { t } = useI18n();
const slots = useSlots();

const isError = computed(() => Boolean(props.error));
const errorMessage = computed(() =>
  typeof props.error === 'string' ? props.error : ''
);
const hasDefaultContent = computed(() => Boolean(slots.default));
const showSkeleton = computed(
  () => props.loading && !isError.value && !hasDefaultContent.value
);
const lastUpdatedDisplay = computed(() => {
  const value = props.lastUpdatedAt;
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) return value.toLocaleString();
  return String(value);
});
const hasLastUpdate = computed(() => lastUpdatedDisplay.value.length > 0);
const hasChrome = computed(
  () => hasLastUpdate.value || Boolean(slots.metrics) || Boolean(slots.filters)
);
const loadingLabel = computed(() => t('LAIS_OPERATIONAL_PAGE.LOADING'));
const resolvedAsideLabel = computed(
  () => props.asideLabel || t('LAIS_OPERATIONAL_PAGE.ASIDE_LABEL')
);
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full flex-col overflow-y-auto bg-lais-bg-page text-lais-text md:overflow-hidden"
  >
    <LaisHeader
      :title="title"
      :helper="helper"
      :breadcrumb="breadcrumb"
      :show-back="showBack"
      class="shrink-0"
      @back="emit('back')"
    >
      <template v-if="slots.actions" #actions>
        <slot name="actions" />
      </template>
    </LaisHeader>

    <div
      v-if="hasChrome"
      class="flex shrink-0 flex-col gap-4 px-4 pb-4 sm:px-6 lg:px-10"
    >
      <LaisLastUpdate
        v-if="hasLastUpdate"
        :value="lastUpdatedDisplay"
        align="left"
      />
      <div v-if="slots.metrics" class="min-w-0">
        <slot name="metrics" />
      </div>
      <div v-if="slots.filters" class="min-w-0">
        <slot name="filters" />
      </div>
    </div>

    <div
      class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden max-md:flex-none max-md:overflow-visible"
    >
      <LaisErrorState
        v-if="isError"
        class="mx-4 my-4 sm:mx-6 lg:mx-10"
        :description="errorMessage"
        :show-default-description="!errorMessage"
        show-action
        @action="emit('retry')"
      />

      <section
        v-else-if="showSkeleton"
        role="status"
        aria-busy="true"
        :aria-label="loadingLabel"
        class="px-4 py-4 sm:px-6 lg:px-10"
      >
        <LaisCardSkeleton :count="skeletonCount" />
      </section>

      <div
        v-else
        class="grid gap-4 px-4 py-4 sm:px-6 lg:px-10"
        :class="slots.aside ? 'lg:grid-cols-[minmax(0,1fr)_20rem]' : ''"
      >
        <div class="min-w-0">
          <slot />
        </div>
        <aside
          v-if="slots.aside"
          :aria-label="resolvedAsideLabel"
          class="min-w-0 lg:sticky lg:top-4 lg:self-start"
        >
          <slot name="aside" />
        </aside>
      </div>
    </div>

    <footer
      v-if="slots.footer"
      class="shrink-0 border-t border-lais-border-soft bg-lais-bg-card px-4 py-3 sm:px-6 lg:px-10"
    >
      <slot name="footer" />
    </footer>
  </div>
</template>
